import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEntries } from 'features/rolodex'
import {
  DEFAULT_ASSET_MATERIAL_MAPPING,
  DEFAULT_ASSET_TYPE_MAPPING,
  DEFAULT_DELIVERABLE_RESOLUTION_MAPPING,
  DEFAULT_INSPECTION_RESOLUTION_MAPPING,
  DEFAULT_INSPECTION_SURFACE_LOCATION_MAPPING,
  DEFAULT_SENSOR_DIAMETER,
  SENSOR_PAYLOAD_TYPE_TO_FREQUENCY,
} from 'pages/reporting/reports/rug/constants'
import {
  CalibrationStep,
  RUGPrePopulatedData,
  SensorPayloadData,
} from 'pages/reporting/reports/rug/rugReportData'
import {
  CalibrationStepName,
  SensorPayloadType,
} from 'pages/reporting/reports/rug/types'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoInspection,
  GeckoInspectionForm,
  GeckoJob,
  GeckoMeasurementUnit,
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  RolodexEntryType,
  RUGInspectionFormData,
  SalesforceJobData,
  TimeString,
} from 'types'

export const DEFAULT_CALIBRATION_STEPS: CalibrationStep[] = [
  {
    stepName: CalibrationStepName.THIN,
    distance: { value: 0.1, unit: GeckoMeasurementUnit.Inches.id },
  },
  {
    stepName: CalibrationStepName.THICK,
    distance: { value: 0.5, unit: GeckoMeasurementUnit.Inches.id },
  },
  {
    stepName: CalibrationStepName.VERIFICATION,
    distance: { value: 0.2, unit: GeckoMeasurementUnit.Inches.id },
  },
]

export const DEFAULT_PAYLOADS: SensorPayloadData[] = [
  {
    payloadType: SensorPayloadType.THIN,
    sensorFrequency: 10.0,
    sensorDiameter: {
      value: 0.125,
      unit: GeckoMeasurementUnit.Inches.id,
    },
  },
]

interface CantileverData {
  unit?: GeckoUnit
  site?: GeckoSite
  organization?: GeckoOrganization
  selectedJob?: GeckoJob
  assetModel?: GeckoAssetModel
  salesforceJobNumber?: string
}

export interface CantileverReportDataResult {
  prePopulatedData: RUGPrePopulatedData
  hasCantileverData: boolean
}

export const getCustomerTitle = (
  organization: GeckoOrganization | undefined,
  site: GeckoSite | undefined
) => {
  return `${organization?.data.name ?? ''} - ${site?.data.name ?? ''}`
}

export const getCustomerLocation = (site: GeckoSite | undefined) => {
  return site?.data.address
    ? `${site.data.address.city}, ${site.data.address.region}`
    : ''
}

export const generateCalibrationTimesFromJobDuration = (
  startDate: Date | undefined,
  endDate: Date | undefined
): TimeString[] => {
  if (!startDate || !endDate) return []

  const calibrationTimes: TimeString[] = []
  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).startOf('day')

  let currentDate = start
  while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
    const calibrationTime = currentDate.hour(7).minute(0).second(0)
    calibrationTimes.push(calibrationTime.toISOString() as TimeString)
    currentDate = currentDate.add(1, 'day')
  }

  return calibrationTimes
}

export const getCantileverPrePopulatedData = (
  cantileverData: CantileverData,
  inspectionForm?: GeckoInspectionForm
): RUGPrePopulatedData => {
  const {
    unit,
    site,
    organization,
    selectedJob,
    assetModel,
    salesforceJobNumber,
  } = cantileverData

  const assetType = assetModel?.data.type
    ? DEFAULT_ASSET_TYPE_MAPPING[assetModel?.data.type]
    : undefined
  const assetName = unit?.data.name ?? ''
  const customerName = getCustomerTitle(organization, site)
  const location = getCustomerLocation(site)
  const inspectionStartDate = selectedJob?.data.start_date
  const inspectionEndDate = selectedJob?.data.end_date

  const defaultAssetMaterial = assetModel?.data.type
    ? DEFAULT_ASSET_MATERIAL_MAPPING[assetModel.data.type]
    : undefined

  const defaultInspectionResolution = assetModel?.data.type
    ? DEFAULT_INSPECTION_RESOLUTION_MAPPING[assetModel.data.type]
    : undefined

  const defaultDeliverableResolution = assetModel?.data.type
    ? DEFAULT_DELIVERABLE_RESOLUTION_MAPPING[assetModel.data.type]
    : undefined

  const defaultInspectionSurface = assetModel?.data.type
    ? DEFAULT_INSPECTION_SURFACE_LOCATION_MAPPING[assetModel.data.type]
    : undefined

  const defaultCalibrationTimes = generateCalibrationTimesFromJobDuration(
    inspectionStartDate,
    inspectionEndDate
  )

  const baseData: RUGPrePopulatedData = {
    assetName,
    assetId: salesforceJobNumber,
    assetType,
    assetMaterial: defaultAssetMaterial,
    inspectionResolution: defaultInspectionResolution,
    deliverableResolution: defaultDeliverableResolution,
    inspectionSurface: defaultInspectionSurface,
    customerName,
    customerLocation: location,
    inspectionStartDate: inspectionStartDate
      ? dayjs(inspectionStartDate).format('MM/DD/YYYY')
      : undefined,
    inspectionEndDate: inspectionEndDate
      ? dayjs(inspectionEndDate).format('MM/DD/YYYY')
      : undefined,
    calibrationTimes: defaultCalibrationTimes,
  }

  if (!inspectionForm) {
    return baseData
  }

  const formContent = inspectionForm.data.content as RUGInspectionFormData

  const calibrationSteps = formContent.calibrationInfo
    ? [
        {
          stepName: CalibrationStepName.THIN,
          distance: {
            value: formContent.calibrationInfo.thinStep.stepThickness,
            unit: formContent.calibrationInfo.thinStep.unit,
          },
        },
        {
          stepName: CalibrationStepName.THICK,
          distance: {
            value: formContent.calibrationInfo.thickStep.stepThickness,
            unit: formContent.calibrationInfo.thickStep.unit,
          },
        },
        {
          stepName: CalibrationStepName.VERIFICATION,
          distance: {
            value: formContent.calibrationInfo.verificationStep.stepThickness,
            unit: formContent.calibrationInfo.verificationStep.unit,
          },
        },
      ]
    : DEFAULT_CALIBRATION_STEPS

  const robots = formContent.robotInfo?.robotSerialNumbers?.map(
    (serialNumber) => ({
      model: formContent.robotInfo.robotType,
      serialNumber,
    })
  )

  const formCalibrationTimes =
    formContent.calibrationInfo?.calibrationTimes?.map(
      (dateTime) => dayjs(dateTime).toISOString() as TimeString
    )
  const calibrationTimes =
    formCalibrationTimes && formCalibrationTimes.length > 0
      ? formCalibrationTimes
      : baseData.calibrationTimes

  const payloadType = formContent.robotInfo?.payloadType as
    | SensorPayloadType
    | undefined
  const sensorFrequency = payloadType
    ? SENSOR_PAYLOAD_TYPE_TO_FREQUENCY[payloadType]
    : undefined

  const sensorDiameter = {
    value: DEFAULT_SENSOR_DIAMETER.value,
    unit: DEFAULT_SENSOR_DIAMETER.unit.id,
  }

  const payloads: SensorPayloadData[] = payloadType
    ? [
        {
          payloadType,
          sensorFrequency,
          sensorDiameter,
        },
      ]
    : []

  return {
    ...baseData,
    assetMaterial: formContent.material || baseData.assetMaterial,
    inspectionTemperature: formContent.surfaceTemperature?.toString(),
    componentAxisStartLocations: {},
    inspectionStartDate: formContent.inspectionStartDate
      ? dayjs(formContent.inspectionStartDate).format('MM/DD/YYYY')
      : baseData.inspectionStartDate,
    inspectionEndDate: formContent.inspectionEndDate
      ? dayjs(formContent.inspectionEndDate).format('MM/DD/YYYY')
      : baseData.inspectionEndDate,
    inspectionResolution:
      formContent.inspectionResolution || baseData.inspectionResolution,
    deliverableResolution:
      formContent.deliverableResolution || baseData.deliverableResolution,
    coverageScope: formContent.coverageScope,
    inspectionSummaryNotes: formContent.notes,
    inspectionSurface:
      formContent.inspectionSurface || baseData.inspectionSurface,
    surfaceCondition: formContent.surfaceCondition,
    coatingPresent: formContent.isCoated === 'Yes',
    procedure: formContent.procedure,
    robots,
    calibrationBlocks: formContent.calibrationInfo
      ? [
          {
            type: formContent.calibrationInfo.calibrationBlockType,
            serialNumber:
              formContent.calibrationInfo.calibrationBlockSerialNumber,
          },
        ]
      : [],
    calibrationTimes: calibrationTimes ?? [],
    calibrationSteps,
    payloads,
    inspectionSoftware: 'Fulcrum',
  }
}

// TODO: Add sensor serial numbers to this as well

export const useCantileverReportData = (
  inspections: readonly GeckoInspection[] | undefined,
  rugInspectionForm?: GeckoInspectionForm
): CantileverReportDataResult => {
  const { unitId, siteId, organizationId, jobId } = useMemo(() => {
    const linkIds: {
      unit: string | undefined
      site: string | undefined
      organization: string | undefined
      job: string | undefined
    } = {
      unit: undefined,
      site: undefined,
      organization: undefined,
      job: undefined,
    }

    if (inspections) {
      for (let i = 0; i < inspections.length; i++) {
        const inspection = inspections[i]
        for (const key of Object.keys(linkIds)) {
          const currVal = linkIds[key as keyof typeof linkIds]
          const newVal = inspection.tags[key as keyof typeof inspection.tags]

          if (!currVal) {
            linkIds[key as keyof typeof linkIds] = newVal
          } else if (newVal !== currVal) {
            message.error(`${key} is not consistent across inspections`)
            return {
              unitId: undefined,
              siteId: undefined,
              organizationId: undefined,
              jobId: undefined,
            }
          }
        }
      }
    }

    return {
      unitId: linkIds.unit,
      siteId: linkIds.site,
      organizationId: linkIds.organization,
      jobId: linkIds.job,
    }
  }, [inspections])

  const { units, sites, organizations, jobs, assetModels } = useRolodexEntries(
    unitId && siteId && organizationId && jobId
      ? [
          {
            ids: [unitId, siteId, organizationId, jobId],
          },
          {
            type: RolodexEntryType.ASSET_MODEL,
            tags: {
              [RolodexEntryType.UNIT]: unitId,
            },
          },
        ]
      : undefined
  )

  const { unit, site, organization, job, assetModel } = useMemo(() => {
    return {
      unit: units.at(0),
      site: sites.at(0),
      organization: organizations.at(0),
      job: jobs.at(0),
      assetModel: assetModels.at(0),
    }
  }, [units, sites, organizations, jobs, assetModels])

  const salesforceJobId = job?.tags[RolodexEntryType.SALESFORCE_JOB]
  const { entries: salesforceJobs } = useRolodexEntries(
    salesforceJobId
      ? {
          type: RolodexEntryType.SALESFORCE_JOB,
          ids: [salesforceJobId],
        }
      : undefined
  )

  const salesforceJobNumber = useMemo(() => {
    const sfJob = salesforceJobs.at(0)
    if (!sfJob) return undefined
    return (sfJob.data as SalesforceJobData).job_number
  }, [salesforceJobs])

  return useMemo(() => {
    const cantileverData = getCantileverPrePopulatedData(
      {
        unit,
        site,
        organization,
        selectedJob: job,
        assetModel,
        salesforceJobNumber,
      },
      rugInspectionForm
    )

    return {
      prePopulatedData: cantileverData,
      hasCantileverData: !!rugInspectionForm,
    }
  }, [
    unit,
    site,
    organization,
    job,
    assetModel,
    salesforceJobNumber,
    rugInspectionForm,
  ])
}
